"use client";

import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageCircle, ArrowRight } from "lucide-react";
import { WHATSAPP_CONSULTOR_DIAGNOSTIC } from "@/lib/constants";

const WHATSAPP_NUMBER = WHATSAPP_CONSULTOR_DIAGNOSTIC;

const STEP_1_OPTIONS = [
  { label: "Planilhas de Excel/Papel", emoji: "📊" },
  { label: "Sistema lento e antigo", emoji: "🐢" },
  { label: "Apenas no WhatsApp dos vendedores", emoji: "📱" },
] as const;

const STEP_2_OPTIONS = [
  { label: "Leads esfriam por falta de retorno rápido", emoji: "❄️" },
  { label: "Falta de visão de quem vende mais", emoji: "👁️" },
  { label: "Desorganização do estoque", emoji: "📦" },
] as const;

const STEP_3_OPTIONS = [
  { label: "Ter automação e alertas de follow-up", emoji: "⚡" },
  { label: "Ter um painel financeiro e de metas claro", emoji: "📈" },
  { label: "Integrar tudo em um só lugar", emoji: "🔗" },
] as const;

const springTransition = {
  type: "spring" as const,
  stiffness: 300,
  damping: 20,
};

const stepVariants = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};

const TOTAL_STEPS = 7;

export interface LeadDiagnosticModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeadDiagnosticModal({
  open,
  onOpenChange,
}: LeadDiagnosticModalProps) {
  const [step, setStep] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(0);
  const [leadName, setLeadName] = useState("");
  const [dealershipName, setDealershipName] = useState("");
  const [pain, setPain] = useState("");
  const [bottleneck, setBottleneck] = useState("");
  const [desire, setDesire] = useState("");

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(0);
      setLeadName("");
      setDealershipName("");
      setPain("");
      setBottleneck("");
      setDesire("");
    }, 300);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(0);
        setLeadName("");
        setDealershipName("");
        setPain("");
        setBottleneck("");
        setDesire("");
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleStep0Submit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = leadName.trim();
    const dealership = dealershipName.trim();
    if (!name || !dealership) return;
    setStep(1);
  };

  const handleWhatsApp = () => {
    const message = `Olá! Tudo bem? Me chamo ${leadName.trim()} e sou da concessionária ${dealershipName.trim()}. Fiz o diagnóstico na página e tenho interesse em melhorar minha gestão e estancar a perda de vendas. O meu maior gargalo hoje é: ${bottleneck}.`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    handleClose();
  };

  const canProceedStep0 = leadName.trim().length > 0 && dealershipName.trim().length > 0;
  const progressPercent = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg mx-4 p-0 gap-0 overflow-hidden bg-black/70 backdrop-blur-xl border border-white/10 rounded-4xl text-white shadow-2xl shadow-primary/10 min-h-[400px] flex flex-col"
        aria-label="Diagnóstico da sua concessionária"
      >
        {/* Barra de progresso no topo */}
        <div className="h-1.5 w-full bg-white/10 rounded-none overflow-hidden shrink-0">
          <motion.div
            className="h-full bg-primary rounded-r-full origin-left"
            style={{ boxShadow: "0 0 20px var(--primary)" }}
            initial={false}
            animate={{ width: `${progressPercent}%` }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            aria-valuenow={step + 1}
            aria-valuemin={1}
            aria-valuemax={TOTAL_STEPS}
            role="progressbar"
          />
        </div>

        <div className="flex-1 px-8 pb-8 pt-6 min-h-[320px]">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="step0"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springTransition}
                className="flex flex-col h-full"
              >
                <h2 className="text-xl font-bold text-white mb-6 text-center">
                  Antes de otimizarmos seus lucros, com quem estamos falando?
                </h2>
                <form onSubmit={handleStep0Submit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="leadName" className="text-gray-400 text-sm">
                      Nome completo
                    </Label>
                    <Input
                      id="leadName"
                      type="text"
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      placeholder="Seu nome"
                      className="h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-primary/50"
                      aria-label="Nome completo"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dealershipName" className="text-gray-400 text-sm">
                      Nome da concessionária
                    </Label>
                    <Input
                      id="dealershipName"
                      type="text"
                      value={dealershipName}
                      onChange={(e) => setDealershipName(e.target.value)}
                      placeholder="Nome da concessionária"
                      className="h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-primary/50"
                      aria-label="Nome da concessionária"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={!canProceedStep0}
                    className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold mt-6 shadow-[0_0_20px_var(--primary)]"
                  >
                    Continuar
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div
                key="step1"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springTransition}
                className="flex flex-col h-full"
              >
                <h2 className="text-xl font-bold text-white mb-6 text-center">
                  Como você controla seus leads e clientes hoje, {leadName.trim() || "?"}?
                </h2>
                <div className="space-y-3">
                  {STEP_1_OPTIONS.map((opt) => (
                    <motion.button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        setPain(opt.label);
                        setStep(2);
                      }}
                      whileTap={{ scale: 0.97 }}
                      className={`w-full p-4 rounded-xl text-left font-medium transition-all flex items-center gap-3 ${
                        pain === opt.label
                          ? "bg-primary/20 border-2 border-primary shadow-[0_0_24px_var(--primary)] text-white"
                          : "bg-white/5 border border-white/10 hover:border-primary/40 hover:bg-white/10 text-white"
                      }`}
                    >
                      <span className="text-2xl" aria-hidden>{opt.emoji}</span>
                      {opt.label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springTransition}
                className="flex flex-col h-full"
              >
                <h2 className="text-xl font-bold text-white mb-6 text-center">
                  Onde a sua operação mais perde dinheiro hoje?
                </h2>
                <div className="space-y-3">
                  {STEP_2_OPTIONS.map((opt) => (
                    <motion.button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        setBottleneck(opt.label);
                        setStep(3);
                      }}
                      whileTap={{ scale: 0.97 }}
                      className={`w-full p-4 rounded-xl text-left font-medium text-sm transition-all flex items-center gap-3 ${
                        bottleneck === opt.label
                          ? "bg-primary/20 border-2 border-primary shadow-[0_0_24px_var(--primary)] text-white"
                          : "bg-white/5 border border-white/10 hover:border-primary/40 hover:bg-white/10 text-white"
                      }`}
                    >
                      <span className="text-2xl" aria-hidden>{opt.emoji}</span>
                      {opt.label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springTransition}
                className="flex flex-col h-full"
              >
                <h2 className="text-xl font-bold text-white mb-6 text-center">
                  Se você pudesse resolver um problema hoje com um estalar de dedos, qual seria?
                </h2>
                <div className="space-y-3">
                  {STEP_3_OPTIONS.map((opt) => (
                    <motion.button
                      key={opt.label}
                      type="button"
                      onClick={() => {
                        setDesire(opt.label);
                        setStep(4);
                      }}
                      whileTap={{ scale: 0.97 }}
                      className={`w-full p-4 rounded-xl text-left font-medium text-sm transition-all flex items-center gap-3 ${
                        desire === opt.label
                          ? "bg-primary/20 border-2 border-primary shadow-[0_0_24px_var(--primary)] text-white"
                          : "bg-white/5 border border-white/10 hover:border-primary/40 hover:bg-white/10 text-white"
                      }`}
                    >
                      <span className="text-2xl" aria-hidden>{opt.emoji}</span>
                      {opt.label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springTransition}
                className="flex flex-col h-full"
              >
                <h2 className="text-xl font-bold text-white mb-4 text-center">
                  O seu potencial é gigante.
                </h2>
                <p className="text-gray-400 text-center mb-6">
                  Concessionárias que abandonam planilhas e adotam automação aumentam a conversão em até <span className="text-primary font-bold">29%</span> no primeiro trimestre.
                </p>
                <motion.div
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="h-2 rounded-full bg-primary/30 overflow-hidden mb-8"
                >
                  <motion.div
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    className="h-full bg-primary rounded-full"
                  />
                </motion.div>
                <Button
                  type="button"
                  onClick={() => setStep(5)}
                  className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                >
                  Avançar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div
                key="step5"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springTransition}
                className="flex flex-col h-full"
              >
                <h2 className="text-xl font-bold text-white mb-4 text-center">
                  O custo da inércia
                </h2>
                <p className="text-gray-400 text-center mb-6">
                  Você sabia que <span className="text-primary font-bold">71%</span> dos leads compram com o vendedor que responde primeiro? Sem alertas automáticos, seu concorrente está levando o seu dinheiro.
                </p>
                <Button
                  type="button"
                  onClick={() => setStep(6)}
                  className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold mt-auto"
                >
                  Ver meu plano de ação
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {step === 6 && (
              <motion.div
                key="step6"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={springTransition}
                className="flex flex-col h-full justify-center"
              >
                <h2 className="text-xl font-bold text-white mb-4 text-center">
                  Você tem a visão certa, {leadName.trim()}.
                </h2>
                <p className="text-gray-400 text-center mb-2">
                  Parabéns por dar o primeiro passo para assumir o controle da {dealershipName.trim()}. Seu diagnóstico está pronto.
                </p>
                <Button
                  type="button"
                  onClick={handleWhatsApp}
                  className="w-full h-14 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg shadow-[0_0_30px_var(--primary)] hover:shadow-[0_0_50px_var(--primary)] transition-all mt-8"
                  aria-label="Falar com o especialista no WhatsApp"
                >
                  <MessageCircle className="w-6 h-6 mr-3" />
                  Falar com o Especialista Agora
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
